import React from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";

// Terms of Service — drafted for NETBINGE, LLC running GlobalEyes.TV. Baseline
// terms suitable for an MVP; should be reviewed by counsel before scale.
const TermsPage = () => (
  <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black text-gray-200 flex flex-col" data-testid="terms-page">
    <div className="container mx-auto px-4 py-12 max-w-3xl flex-1">
      <Link to="/" className="text-emerald-400 hover:text-emerald-300 text-sm">← Back home</Link>
      <h1 className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-2">Terms of Service</h1>
      <p className="text-gray-500 text-sm mb-8">Last updated: May 10, 2026 · NETBINGE, LLC</p>

      <div className="prose prose-invert prose-sm sm:prose-base max-w-none space-y-6">
        <section>
          <h2 className="text-xl text-white font-semibold">1. Acceptance</h2>
          <p>
            By accessing or using GlobalEyes.TV (the "Service"), operated by <strong>NETBINGE, LLC</strong>, you agree to these
            Terms of Service. If you don't agree, please don't use the Service.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">2. The Service</h2>
          <p>
            GlobalEyes.TV lets you record short looping videos, attach them to a world map, build a personal "TV station" at
            globaleyes.tv/{`<your username>`}, and exchange credits for gifts, sponsorships, and commissions.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">3. Eligibility</h2>
          <p>
            You must be at least 13 years old to use the Service. If you are between 13 and 18, you must have a parent or legal
            guardian's permission. By registering, you represent that all information you provide is accurate.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">4. Your account</h2>
          <ul className="list-disc ml-6 space-y-1">
            <li>You are responsible for keeping your password secure.</li>
            <li>You are responsible for all activity that happens under your account.</li>
            <li>One account per person; multi-accounting to game referrals, gifts, or commissions is prohibited.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">5. Content rules</h2>
          <p>You agree NOT to upload, post, or share:</p>
          <ul className="list-disc ml-6 space-y-1">
            <li>Content that is unlawful, harassing, hateful, threatening, or defamatory.</li>
            <li>Sexually explicit content or content depicting minors in a sexual manner.</li>
            <li>Content that infringes someone else's copyright, trademark, or right of publicity.</li>
            <li>Spam, phishing, malware, or deceptive content (including AI-generated content that misrepresents itself as a real human moment — GlobalEyes is for real humans).</li>
            <li>Personal information of others without their consent (doxing).</li>
            <li>Content that violates any law in your jurisdiction.</li>
          </ul>
          <p className="mt-2">
            You retain ownership of content you upload. By uploading, you grant NETBINGE, LLC a worldwide, non-exclusive, royalty-free
            license to host, store, transmit, display, and (as needed) modify the content to operate the Service.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">6. Credits and payments</h2>
          <ul className="list-disc ml-6 space-y-1">
            <li>Credits are an in-platform unit of account; they have <strong>no cash value</strong> and cannot be redeemed for cash except
              via specific producer-monetization features expressly offered by the Service.</li>
            <li>Top-ups, subscriptions, gifts, commissions, and wishlist contributions are processed via Stripe. All charges are in USD
              unless otherwise specified.</li>
            <li>Refunds may be issued at our discretion. For commissions that are cancelled before completion, the original payer is
              automatically refunded to the original payment method through Stripe.</li>
            <li>Subscription tiers grant a monthly bucket of credits; unused monthly credits expire each cycle. Lasting (top-up,
              referral, gift) credits do not expire.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">7. Commissions</h2>
          <p>
            Commissions let Executive Producers (EPs) escrow credits in exchange for a producer filming a real-world moment matching
            the EP's spec. Once a producer locks the commission, they have a limited window to film and publish. Funds are released
            on publish. Cancelled or expired commissions are refunded to the EP, less any platform fees disclosed at the time of
            posting.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">8. Gifts and wishlists</h2>
          <p>
            Gift magic links expire after 30 days if unclaimed; unclaimed credits are returned to the sender. Wishlist contributions
            are non-refundable except where required by law.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">9. Enforcement</h2>
          <p>
            We may remove content, suspend accounts, freeze credits, or terminate the Service for any user who violates these Terms
            or applicable law. Where possible we will give notice; serious or repeat violations may result in immediate action.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">10. Disclaimers</h2>
          <p>
            The Service is provided "AS IS" and "AS AVAILABLE", without warranties of any kind, express or implied. We don't warrant
            uninterrupted, error-free, or secure operation. User-generated content is the responsibility of the user who posted it,
            not NETBINGE, LLC.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">11. Limitation of liability</h2>
          <p>
            To the maximum extent permitted by law, NETBINGE, LLC's total liability for any claim arising out of or related to the
            Service shall not exceed the greater of (a) the amount you paid us in the 12 months preceding the claim, or (b) USD 100.
            We are not liable for indirect, incidental, special, consequential, or punitive damages.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">12. Governing law</h2>
          <p>
            These Terms are governed by the laws of the State of Delaware, USA, without regard to its conflict-of-laws principles. Any
            dispute will be resolved exclusively in the state or federal courts located in Delaware, and you consent to that
            jurisdiction.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">13. Changes</h2>
          <p>
            We may modify these Terms from time to time. We'll post material changes on the Site. Continued use after a change means
            you accept the updated Terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl text-white font-semibold">14. Contact</h2>
          <p>
            Questions about these Terms? See our <Link to="/contact" className="text-emerald-400">Contact page</Link>.
          </p>
        </section>
      </div>
    </div>
    <Footer />
  </div>
);

export default TermsPage;
